<template>
  <div>

    <div class="container">
      <div class="box-title">
        <h4>
          <i class="fal fa-list"></i>
          ລາຍການເຄື່ອນໄຫວທັງໝົດ
          <span class="countTotal">({{ totalTransactions }})</span>
        </h4>
      </div>

      <!-- Filters -->
      <div class="box">
        <div class="columns is-multiline">
          <div class="column is-2">
            <div class="control">
              <label class="label">ເລກບິນ</label>
              <input type="text" class="input" v-model="filters.shipment_number" placeholder="ຄົ້ນຫາເລກບິນ" @keyup.enter="applyFilters">
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ສາຂາ</label>
              <BranchSelect v-model="filters.branch_id" placeholder="ທັງໝົດ" @input="applyFilters" />
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ປະເພດກະເປົ໋າ</label>
              <div class="select">
                <select v-model="filters.wallet_type" @change="applyFilters">
                  <option value="">ທັງໝົດ</option>
                  <option value="FREIGHT">ຄ່າຂົນສົ່ງ</option>
                  <option value="COD">COD</option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ປະເພດລາຍການ</label>
              <div class="select">
                <select v-model="filters.transaction_type" @change="applyFilters">
                  <option value="">ທັງໝົດ</option>
                  <option value="BANK">ທຸລະກຳທະນາຄານ (ເຕີມເງິນ + ໂອນ)</option>
                  <option value="TOP_UP">ເຕີມເງິນ</option>
                  <option value="DEDUCT">ຫັກເງິນ</option>
                  <option value="REFUND">ຄືນເງິນ</option>
                  <option value="ROLLBACK">ຍ້ອນກັບ</option>
                  <option value="TRANSFER_IN">ເຂົ້າ (TRANSFER_IN)</option>
                  <option value="TRANSFER_OUT">ອອກ (TRANSFER_OUT)</option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ຈາກວັນທີ</label>
              <input type="date" class="input" v-model="filters.from_date" @change="applyFilters">
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ຫາວັນທີ</label>
              <input type="date" class="input" v-model="filters.to_date" @change="applyFilters">
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">&nbsp;</label>
              <div class="buttons action-buttons">
                <vs-button @click="applyFilters" :loading="isLoading">
                  <i class="fal fa-search"></i> ຄົ້ນຫາ
                </vs-button>
                <vs-button flat @click="exportToExcel" :loading="isExporting" :disabled="transactions.length === 0">
                  <i class="fal fa-download"></i> Excel
                </vs-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Export History -->
      <div v-if="exportHistory.length > 0" class="box export-history">
        <div class="export-history-header" @click="showExportHistory = !showExportHistory">
          <strong><i class="fal fa-history"></i> Export History</strong>
          <span class="history-count">({{ exportHistory.length }})</span>
          <i class="fal" :class="showExportHistory ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>
        <div v-show="showExportHistory" class="export-history-list">
          <div v-for="job in exportHistory" :key="job.id" class="history-item">
            <div class="history-info">
              <span class="history-filename">{{ getFileName(job.file_path) }}</span>
              <span class="history-date">{{ formatDate(job.created_at) }}</span>
              <span class="history-rows">{{ job.total_rows }} rows</span>
              <span v-if="job.expires_at" class="history-expires">
                Expires: {{ formatDate(job.expires_at) }}
              </span>
            </div>
            <vs-button size="small" @click="downloadHistoryExport(job.id)" :loading="downloadingJobId === job.id">
              <i class="fal fa-download"></i> Download
            </vs-button>
          </div>
        </div>
      </div>

      <!-- Export Progress -->
      <div v-if="exportJob" class="box export-progress">
        <div class="export-header">
          <strong><i class="fal fa-file-export"></i> Export Progress</strong>
          <span class="export-status" :class="(exportJob.status || '').toLowerCase()">
            {{ getExportStatusLabel(exportJob.status) }}
          </span>
        </div>
        <div v-if="exportJob.status === 'PROCESSING'" class="progress-bar">
          <div class="progress-fill" :style="{ width: exportJob.progress + '%' }"></div>
        </div>
        <div class="export-info">
          <span>{{ exportJob.processed_rows }} / {{ exportJob.total_rows }} rows</span>
          <span v-if="exportJob.progress > 0">({{ exportJob.progress.toFixed(1) }}%)</span>
        </div>
        <div v-if="exportJob.status === 'COMPLETED'" class="export-actions">
          <vs-button @click="downloadExport" :loading="isDownloading">
            <i class="fal fa-download"></i> Download
          </vs-button>
          <vs-button flat @click="clearExportJob">
            <i class="fal fa-times"></i> Close
          </vs-button>
        </div>
        <div v-if="exportJob.status === 'FAILED'" class="export-error">
          <i class="fal fa-exclamation-triangle"></i>
          {{ exportJob.error_message || 'Export failed' }}
          <vs-button flat @click="clearExportJob">Close</vs-button>
        </div>
      </div>

      <!-- Error Panel -->
      <div v-if="fetchError" class="box">
        <div class="notification is-warning">
          <p>{{ fetchError }}</p>
          <p v-if="fetchErrorRequestId" class="error-ref">ref: {{ fetchErrorRequestId }}</p>
          <vs-button @click="fetchTransactions" class="mt-3">
            <i class="fal fa-redo"></i> ລອງໃໝ່
          </vs-button>
        </div>
      </div>

      <!-- Filter-aware money total (degrades to nothing when the API omits `totals`). -->
      <WalletListTotals :totals="totals" />

      <!-- Table -->
      <div class="center">
        <vs-table>
          <template #thead>
            <vs-tr>
              <vs-th>ວັນທີ</vs-th>
              <vs-th>ສາຂາ</vs-th>
              <vs-th>ກະເປົ໋າ</vs-th>
              <vs-th>ປະເພດ</vs-th>
              <vs-th>ຈຳນວນ</vs-th>
              <vs-th>ຍອດກ່ອນ</vs-th>
              <vs-th>ຍອດຫລັງ</vs-th>
              <vs-th>ເລກອ້າງອີງ</vs-th>
              <vs-th>ເລກທະນາຄານ</vs-th>
              <vs-th>ເລກບິນ</vs-th>
              <vs-th>ສະຖານະສຳເລັດ</vs-th>
              <vs-th>ເສັ້ນທາງ</vs-th>
              <vs-th>ຜູ້ສົ່ງ</vs-th>
              <vs-th>ເບີຜູ້ສົ່ງ</vs-th>
              <vs-th>ຜູ້ຮັບ</vs-th>
              <vs-th>ເບີຜູ້ຮັບ</vs-th>
              <vs-th>ປະເພດຈ່າຍ</vs-th>
              <vs-th>Pre-Order</vs-th>
              <vs-th>ໝາຍເຫດ</vs-th>
              <vs-th>ສະລິບ</vs-th>
              <vs-th>ຈັດການ</vs-th>
            </vs-tr>
          </template>
          <template #tbody>
            <vs-tr v-for="tx in transactions" :key="tx.id">
              <vs-td>{{ formatDate(tx.created_at) }}</vs-td>
              <vs-td>
                <span class="branch-info">
                  <strong>{{ tx.branch_prefix }}</strong>
                  <br>
                  <small>{{ tx.branch_name }}</small>
                </span>
              </vs-td>
              <vs-td>
                <span :class="['wallet-type', (tx.wallet_type || '').toLowerCase()]">
                  {{ getWalletTypeLabel(tx.wallet_type) }}
                </span>
              </vs-td>
              <vs-td>
                <span :class="['tx-type', isCredit(tx) ? 'credit' : 'debit']">
                  {{ getTransactionTypeLabel(tx.transaction_type) }}
                </span>
              </vs-td>
              <vs-td>
                <span :class="['tx-amount', isCredit(tx) ? 'credit' : 'debit']">
                  {{ isCredit(tx) ? '+' : '-' }}{{ formatCurrency(tx.amount) }}
                </span>
              </vs-td>
              <vs-td>{{ formatCurrency(tx.balance_before) }}</vs-td>
              <vs-td>{{ formatCurrency(tx.balance_after) }}</vs-td>
              <vs-td>
                <span class="reference">{{ tx.ref_no }}</span>
              </vs-td>
              <vs-td>
                <span v-if="tx.bank_ref_no" class="reference">{{ tx.bank_ref_no }}</span>
                <span v-else class="no-data">-</span>
              </vs-td>
              <vs-td>
                <span v-if="tx.shipment_number" class="reference">{{ tx.shipment_number }}</span>
                <span v-else class="no-data">-</span>
              </vs-td>
              <vs-td>
                <span v-if="tx.shipment_success_type" :class="['success-type-badge', tx.shipment_success_type]">
                  {{ getSuccessTypeLabel(tx.shipment_success_type) }}
                </span>
                <span v-else class="no-data">-</span>
              </vs-td>
              <vs-td>{{ routeLabel(tx) }}</vs-td>
              <vs-td>{{ tx.sender_customer_name }}</vs-td>
              <vs-td>{{ tx.sender_customer_tel }}</vs-td>
              <vs-td>{{ tx.receiver_customer_name }}</vs-td>
              <vs-td>{{ tx.receiver_customer_tel }}</vs-td>
              <vs-td>{{ payTypeLabel(tx.shipment_pay_type) }}</vs-td>
              <vs-td>
                <span v-if="preOrderLabel(tx.shipment_is_pre_order)" class="preorder-badge">
                  {{ preOrderLabel(tx.shipment_is_pre_order) }}
                </span>
              </vs-td>
              <vs-td>
                <span class="notes" :title="tx.notes">
                  {{ truncate(tx.notes, 30) }}
                </span>
              </vs-td>
              <vs-td>
                <vs-button
                    v-if="hasSlip(tx)"
                    flat
                    icon
                    size="small"
                    @click="viewSlip(tx)"
                    :loading="loadingSlipId === tx.id"
                    title="ເບິ່ງສະລິບ"
                >
                  <i class="fal fa-image" style="color: #17a2b8"></i>
                </vs-button>
                <span v-else class="no-slip">-</span>
              </vs-td>
              <vs-td>
                <span v-if="tx.is_reversed" class="reversed-badge">ກັບຄືນແລ້ວ</span>
                <!-- Reverse/rollback: SuperAdmin/accountingmanager only. UI-only gate — Go still
                     authorizes reverse for the view-only accounting roles (not enforced at Go). -->
                <vs-button
                    v-else-if="canReverse(tx) && $can(['SuperAdmin', 'accountingmanager'])"
                    flat
                    icon
                    size="small"
                    @click="openReverseDialog(tx)"
                    title="ກັບຄືນລາຍການ"
                >
                  <i class="fal fa-undo" style="color: #dc3545"></i>
                </vs-button>
                <span v-else class="no-data">-</span>
              </vs-td>
            </vs-tr>
          </template>
          <template #notFound>
            <div class="not-found">ບໍ່ພົບລາຍການ</div>
          </template>
        </vs-table>

        <!-- Pagination -->
        <vs-pagination
            v-if="totalPages > 1"
            v-model="currentPage"
            :length="totalPages"
            @input="fetchTransactions"
        />
      </div>
    </div>

    <!-- Slip Image Viewer -->
    <vs-dialog v-model="showSlipViewer" width="600px">
      <template #header>
        <h4 class="dialog-title">
          <i class="fal fa-image"></i> ສະລິບການໂອນ
        </h4>
      </template>
      <div class="slip-viewer-content">
        <img v-if="slipViewerUrl" :src="slipViewerUrl" alt="ສະລິບ">
      </div>
      <template #footer>
        <div class="dialog-footer">
          <vs-button flat @click="showSlipViewer = false">ປິດ</vs-button>
        </div>
      </template>
    </vs-dialog>

    <!-- Reverse Transaction Dialog -->
    <ReverseTransactionDialog
        v-if="reverseTarget"
        :transaction="reverseTarget"
        @close="reverseTarget = null"
        @reversed="onReversed"
    />
  </div>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import { formatCurrency, formatErrorMessage, formatDate, truncate, isCredit } from '@/services/walletFormatters';
import { payTypeLabel, preOrderLabel, routeLabel } from '@/services/walletTransactionLabels';
import { POLLING_INTERVAL } from '@/services/walletConstants';
import ReverseTransactionDialog from './Dialog/ReverseTransaction.vue';
import WalletListTotals from './WalletListTotals.vue';
import BranchSelect from '@/components/BranchSelect.vue';

export default {
  name: 'AllTransactions',
  components: { ReverseTransactionDialog, WalletListTotals, BranchSelect },
  data() {
    return {
      transactions: [],
      totals: null,
      totalTransactions: 0,
      totalPages: 1,
      currentPage: 1,
      pageSize: 20,
      isLoading: false,
      fetchError: null,
      fetchErrorRequestId: null,
      isExporting: false,
      isDownloading: false,
      exportJob: null,
      exportPollTimer: null,
      exportHistory: [],
      showExportHistory: false,
      downloadingJobId: null,
      abortController: null,
      showSlipViewer: false,
      slipViewerUrl: null,
      loadingSlipId: null,
      reverseTarget: null,
      filters: {
        branch_id: '',
        wallet_type: '',
        transaction_type: '',
        shipment_number: '',
        from_date: '',
        to_date: '',
      },
      transactionTypeLabels: {
        TOP_UP: 'ເຕີມເງິນ',
        DEDUCT: 'ຫັກເງິນ',
        REFUND: 'ຄືນເງິນ',
        ROLLBACK: 'ຍ້ອນກັບ',
        ADJUSTMENT_CREDIT: 'ປັບຍອດ+',
        ADJUSTMENT_DEBIT: 'ປັບຍອດ-',
        TRANSFER_IN: 'ເຂົ້າ (TRANSFER_IN)',
        TRANSFER_OUT: 'ອອກ (TRANSFER_OUT)',
      },
      exportStatusLabels: {
        PENDING: 'Pending',
        PROCESSING: 'Processing',
        COMPLETED: 'Completed',
        FAILED: 'Failed',
      },
    };
  },
  created() {
    // Search-driven: no auto-load of transactions on open; the user clicks ຄົ້ນຫາ.
    this.setDefaultDates();
    this.restoreExportJob();
    this.fetchExportHistory();
  },
  methods: {
    canReverse(tx) {
      // The server decides — the client CANNOT. Telling a reversible hand-typed cash slip from a
      // refused bank top-up needs top_up_type (not on the wire) and the hw_top_up_requests link
      // (a DB lookup). An absent is_reversible means "this endpoint did not compute it", so the
      // strict === true keeps the button hidden rather than offering a guaranteed 400.
      return tx.is_reversible === true;
    },
    openReverseDialog(tx) {
      this.reverseTarget = tx;
    },
    onReversed() {
      this.reverseTarget = null;
      this.fetchTransactions();
    },
    setDefaultDates() {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      this.filters.to_date = todayStr;
      this.filters.from_date = todayStr;
    },
    applyFilters() {
      // A new search/filter change must start at page 1 so the first-page-only totals refresh
      // for the new filter; paging keeps calling fetchTransactions directly to retain them.
      this.currentPage = 1;
      this.fetchTransactions();
    },
    async fetchTransactions() {
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.isLoading = true;
      this.fetchError = null;
      this.fetchErrorRequestId = null;
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize,
        };

        if (this.filters.branch_id) params.branch_id = this.filters.branch_id;
        if (this.filters.wallet_type) params.wallet_type = this.filters.wallet_type;
        if (this.filters.transaction_type) params.transaction_type = this.filters.transaction_type;
        if (this.filters.shipment_number) params.shipment_number = this.filters.shipment_number;
        if (this.filters.from_date) params.from_date = this.filters.from_date;
        if (this.filters.to_date) params.to_date = this.filters.to_date;

        const response = await walletService.getAllTransactions(
          this.$axios, params, this.abortController.signal
        );

        this.transactions = response.data || [];
        this.totalTransactions = response.meta?.total || 0;
        this.totalPages = response.meta?.total_pages || 1;
        // Totals ride only on page 1 (filter-aware over the whole set); retain them while paging.
        if (this.currentPage === 1) {
          this.totals = response.totals || null;
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') return;
        console.error('Failed to fetch transactions:', error);
        const { message, requestId } = extractErrorInfo(error);
        this.fetchError = message || 'ບໍ່ສາມາດໂຫລດຂໍ້ມູນໄດ້';
        this.fetchErrorRequestId = requestId;
      } finally {
        this.isLoading = false;
      }
    },
    formatDate,






    formatCurrency(amount) {
      return formatCurrency(amount);
    },
    truncate,
    isCredit,
    payTypeLabel,
    preOrderLabel,
    routeLabel,
    hasSlip(tx) {
      // Manual top-ups and inter-wallet transfers both carry a required slip image.
      return tx.reference_type === 'MANUAL_TOPUP' || tx.reference_type === 'INTER_WALLET_TRANSFER';
    },
    async viewSlip(tx) {
      if (this.slipViewerUrl) {
        URL.revokeObjectURL(this.slipViewerUrl);
        this.slipViewerUrl = null;
      }

      this.loadingSlipId = tx.id;
      try {
        const result = await walletService.getTransactionSlip(this.$axios, tx.id);

        if (!result) {
          this.$notification.OpenNotification_Error(
            'top-right', 'warning', 3000, 'ບໍ່ມີສະລິບແນບ'
          );
          return;
        }

        if (result.contentType.includes('application/pdf')) {
          window.open(result.url, '_blank');
          setTimeout(() => URL.revokeObjectURL(result.url), 5000);
          return;
        }

        this.slipViewerUrl = result.url;
        this.showSlipViewer = true;
      } catch (error) {
        this.$notification.OpenNotification_Error(
          'top-right', 'danger', 3000,
          formatErrorMessage(error, 'ບໍ່ສາມາດໂຫລດສະລິບໄດ້')
        );
      } finally {
        this.loadingSlipId = null;
      }
    },
    getWalletTypeLabel(type) {
      const labels = { FREIGHT: 'ຄ່າຂົນສົ່ງ', COD: 'COD' };
      return labels[type] || type;
    },
    getTransactionTypeLabel(type) {
      return this.transactionTypeLabels[type] || type;
    },
    getSuccessTypeLabel(type) {
      const labels = {
        normal: 'ສຳເລັດ',
        split: 'ຈັດສົ່ງແບບແຍກ',
        returned: 'ຕີກັບສຳເລັດ',
        returned_split: 'ຈັດສົ່ງຕີກັບສຳເລັດແຍກ',
      };
      return labels[type] || type;
    },
    async exportToExcel() {
      await this.startAsyncExport();
    },
    async startAsyncExport() {
      this.isExporting = true;
      try {
        const filters = {};
        if (this.filters.branch_id) filters.branch_id = parseInt(this.filters.branch_id);
        if (this.filters.wallet_type) filters.wallet_type = this.filters.wallet_type;
        if (this.filters.transaction_type) filters.transaction_type = this.filters.transaction_type;
        if (this.filters.from_date) filters.from_date = this.filters.from_date;
        if (this.filters.to_date) filters.to_date = this.filters.to_date;

        const response = await walletService.createExportJob(this.$axios, 'TRANSACTIONS', filters);
        this.exportJob = response.data;
        this.saveExportJob();
        this.startExportPolling();
      } catch (error) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, formatErrorMessage(error, 'Export failed'));
      } finally {
        this.isExporting = false;
      }
    },
    startExportPolling() {
      if (this.exportPollTimer) {
        clearInterval(this.exportPollTimer);
      }

      const pollFn = async () => {
        if (!this.exportJob) {
          this.stopExportPolling();
          return;
        }

        try {
          const response = await walletService.getExportJob(this.$axios, this.exportJob.id);
          this.exportJob = response.data;
          this.saveExportJob();

          if (this.exportJob.status === 'COMPLETED' || this.exportJob.status === 'FAILED') {
            this.stopExportPolling();
            this.clearSavedExportJob();
            if (this.exportJob.status === 'COMPLETED') {
              this.fetchExportHistory();
            } else if (this.exportJob.status === 'FAILED') {
              this.$notification.OpenNotification_Error('top-right', 'danger', 5000, 'Export failed: ' + (this.exportJob.error_message || 'An error occurred'));
            }
          }
        } catch (error) {
          console.error('Poll error:', error);
        }
      };

      pollFn();
      this.exportPollTimer = setInterval(pollFn, POLLING_INTERVAL);
    },
    saveExportJob() {
      if (this.exportJob) {
        try {
          localStorage.setItem('wallet_export_job', JSON.stringify(this.exportJob));
        } catch (e) {
          console.warn('Failed to persist export job to localStorage:', e.message);
        }
      }
    },
    clearSavedExportJob() {
      localStorage.removeItem('wallet_export_job');
    },
    restoreExportJob() {
      try {
        const saved = localStorage.getItem('wallet_export_job');
        if (saved) {
          const job = JSON.parse(saved);
          if (job && job.id && (job.status === 'PENDING' || job.status === 'PROCESSING')) {
            this.exportJob = job;
            this.startExportPolling();
          } else {
            this.clearSavedExportJob();
          }
        }
      } catch (e) {
        console.warn('Failed to restore export job from localStorage:', e.message);
      }
    },
    async fetchExportHistory() {
      try {
        const response = await walletService.listExportJobs(this.$axios, 10);
        this.exportHistory = (response.data || []).filter(job => job.status === 'COMPLETED');
      } catch (error) {
        console.error('Failed to fetch export history:', error);
      }
    },
    async downloadHistoryExport(jobId) {
      this.downloadingJobId = jobId;
      try {
        await walletService.downloadExport(this.$axios, jobId);
      } catch (error) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, formatErrorMessage(error, 'Download failed - file may have expired'));
      } finally {
        this.downloadingJobId = null;
      }
    },
    stopExportPolling() {
      if (this.exportPollTimer) {
        clearInterval(this.exportPollTimer);
        this.exportPollTimer = null;
      }
    },
    async downloadExport() {
      if (!this.exportJob || this.exportJob.status !== 'COMPLETED') {
        return;
      }

      this.isDownloading = true;
      try {
        await walletService.downloadExport(this.$axios, this.exportJob.id);
      } catch (error) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, formatErrorMessage(error, 'Download failed'));
      } finally {
        this.isDownloading = false;
      }
    },
    clearExportJob() {
      this.stopExportPolling();
      this.exportJob = null;
    },
    getExportStatusLabel(status) {
      return this.exportStatusLabels[status] || status;
    },
    getFileName(filePath) {
      if (!filePath) return '-';
      return filePath.split('/').pop();
    },
  },
  beforeDestroy() {
    this.stopExportPolling();
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.slipViewerUrl) {
      URL.revokeObjectURL(this.slipViewerUrl);
      this.slipViewerUrl = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.container {
  margin-top: 20px;
}

.box-title {
  margin-bottom: 20px;

  h4 {
    font-size: 18px;
    font-weight: 600;

    i {
      margin-right: 8px;
      color: #17a2b8;
    }
  }
}

.countTotal {
  color: #666;
  font-weight: normal;
}

.error-ref {
  font-size: 12px;
  color: #856404;
  margin-top: 4px;
}

.mt-3 {
  margin-top: 1rem;
}

.branch-info {
  small {
    color: #666;
  }
}

.wallet-type {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;

  &.freight {
    background: #e3f2fd;
    color: #1565c0;
  }

  &.cod {
    background: #fff3e0;
    color: #e65100;
  }
}

.tx-type {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;

  &.credit {
    background: #d4edda;
    color: #155724;
  }

  &.debit {
    background: #f8d7da;
    color: #721c24;
  }
}

.tx-amount {
  font-weight: 600;

  &.credit {
    color: #28a745;
  }

  &.debit {
    color: #dc3545;
  }
}

.reference {
  font-size: 11px;
  color: #666;
  font-family: monospace;
}

.success-type-badge {
  display: inline-block;
  margin-left: 4px;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  vertical-align: middle;

  &.normal {
    background: #d4edda;
    color: #155724;
  }

  &.split {
    background: #cce5ff;
    color: #004085;
  }

  &.returned {
    background: #fff3cd;
    color: #856404;
  }

  &.returned_split {
    background: #f8d7da;
    color: #721c24;
  }
}

.notes {
  font-size: 12px;
  color: #666;
}

.no-data {
  color: #999;
}

.no-slip {
  color: #999;
}

.reversed-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #e2e3e5;
  color: #6c757d;
}

.preorder-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #e7f1ff;
  color: #0d6efd;
}

.slip-viewer-content {
  text-align: center;
  padding: 16px;

  img {
    max-width: 100%;
    max-height: 500px;
    border-radius: 8px;
    border: 1px solid #ddd;
  }
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;

  i {
    margin-right: 8px;
    color: #17a2b8;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.not-found {
  text-align: center;
  padding: 40px;
  color: #999;
}

.action-buttons {
  i {
    margin-right: 6px;
  }
}

.card-tabs {
  ul li a {
    cursor: pointer;

    &:hover {
      color: #17a2b8;
    }

    i {
      margin-right: 6px;
    }
  }

  li.is-active a {
    border-bottom-color: #17a2b8;
    color: #17a2b8;
  }
}

.export-progress {
  margin-bottom: 20px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;

  i {
    margin-right: 6px;
  }

  .export-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    i {
      color: #17a2b8;
    }
  }

  .export-status {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;

    &.pending {
      background: #fff3cd;
      color: #856404;
    }

    &.processing {
      background: #cce5ff;
      color: #004085;
    }

    &.completed {
      background: #d4edda;
      color: #155724;
    }

    &.failed {
      background: #f8d7da;
      color: #721c24;
    }
  }

  .progress-bar {
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;

    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #17a2b8, #28a745);
      transition: width 0.3s ease;
    }
  }

  .export-info {
    font-size: 13px;
    color: #666;
  }

  .export-actions {
    margin-top: 12px;
    display: flex;
    gap: 10px;
  }

  .export-error {
    margin-top: 10px;
    padding: 10px;
    background: #f8d7da;
    color: #721c24;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
      color: #dc3545;
    }
  }
}

.export-history {
  margin-bottom: 20px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;

  .export-history-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 10px;
    cursor: pointer;
    user-select: none;

    &:hover {
      opacity: 0.8;
    }

    i {
      color: #6c757d;
      margin-right: 6px;
    }

    .fa-chevron-up,
    .fa-chevron-down {
      margin-left: auto;
      margin-right: 0;
    }

    .history-count {
      color: #6c757d;
      font-weight: normal;
      font-size: 14px;
    }
  }

  .export-history-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid #e9ecef;
  }

  .history-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #e9ecef;

    &:hover {
      border-color: #17a2b8;
    }

    i {
      margin-right: 6px;
    }
  }

  .history-info {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .history-filename {
    font-size: 13px;
    font-weight: 500;
    color: #212529;
    font-family: monospace;
  }

  .history-date {
    font-size: 12px;
    color: #6c757d;
  }

  .history-rows {
    font-size: 12px;
    color: #6c757d;
    background: #e9ecef;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .history-expires {
    font-size: 11px;
    color: #856404;
    background: #fff3cd;
    padding: 2px 8px;
    border-radius: 4px;
  }
}
</style>
